import {useQuery} from '@apollo/client';
import * as React from 'react';
import {Link, useParams} from 'react-router';

import {
  CourseDetail,
  aulasConcluidas,
  iconeDeAula,
  modulosDoCurso,
  posterDoCurso,
  statusDoCurso,
  tipoDeAula,
  totalDeAulas,
} from '../coursesList';
import {QUERY_STUDENT_COURSE} from '../studentQueries';
import {StudentTheme} from '../studentTheme';

interface CourseData {
  course?: CourseDetail | null;
}

interface Props {
  theme: StudentTheme;
}

export const CourseDetailScreen: React.FC<Props> = ({theme}) => {
  const {courseId} = useParams();

  const {data, loading, error, refetch} = useQuery<CourseData>(QUERY_STUDENT_COURSE, {
    variables: {id: courseId},
    skip: !courseId,
  });

  const curso = data?.course;
  const modulos = modulosDoCurso(curso);
  const total = totalDeAulas(curso);
  const concluidas = aulasConcluidas(curso);
  const poster = curso ? posterDoCurso(curso) : null;

  return (
    <div className="curso-detalhe">
      <Link to=".." relative="path" className="wod-detalhe__voltar" style={{color: theme.primary}}>
        <i className="fa-light fa-chevron-left" aria-hidden="true" /> Voltar
      </Link>

      {loading && <p className="student-screen__estado">Carregando…</p>}

      {error && !loading && (
        <div className="student-screen__estado">
          <p>Não foi possível carregar o curso.</p>
          <button type="button" className="student-app__retry" onClick={() => refetch()}>
            Tentar de novo
          </button>
        </div>
      )}

      {!loading && !error && !curso && <p className="student-screen__estado">Curso não encontrado.</p>}

      {!loading && !error && curso && (
        <article className="wod-detalhe__conteudo" style={{backgroundColor: theme.body, borderColor: theme.border}}>
          {poster && <img className="curso-detalhe__capa" src={poster} alt="" loading="lazy" />}

          <h2 className="wod-card__nome">{curso.title}</h2>

          <div className="curso-detalhe__meta" style={{color: theme.neutral}}>
            {statusDoCurso(curso.courseStatus) && <span>{statusDoCurso(curso.courseStatus)}</span>}
            {total > 0 && (
              <span>
                {concluidas} de {total} {total === 1 ? 'aula' : 'aulas'}
              </span>
            )}
          </div>

          {curso.description && <p className="wod-card__descricao">{curso.description}</p>}

          {curso.trailer?.uri && (
            <a
              className="wod-detalhe__video"
              style={{color: theme.primary}}
              href={curso.trailer.uri}
              target="_blank"
              rel="noreferrer"
            >
              <i className="fa-light fa-circle-play" aria-hidden="true" /> Assistir ao trailer
            </a>
          )}

          {modulos.length === 0 && (
            <p className="student-screen__estado" style={{color: theme.neutral}}>
              Este curso ainda não tem aulas publicadas.
            </p>
          )}

          {modulos.map((modulo) => (
            <section key={modulo.id} className="wod-bloco" style={{borderColor: theme.border}}>
              {modulo.title && <h3 className="wod-bloco__nome">{modulo.title}</h3>}
              {modulo.description && <p className="wod-bloco__descricao">{modulo.description}</p>}

              <ul className="curso-aulas">
                {(modulo.units ?? []).map((unit) => (
                  <li key={unit.id} className="curso-aula">
                    <Link to={`aula/${unit.id}`} className="curso-aula__link" style={{color: theme.text}}>
                      <i
                        className={`fa-light ${iconeDeAula(unit)} curso-aula__icone`}
                        style={{color: theme.primary}}
                        aria-hidden="true"
                      />

                      <span className="curso-aula__texto">
                        <span className="curso-aula__titulo">{unit.title}</span>
                        {tipoDeAula(unit) && (
                          <span className="curso-aula__tipo" style={{color: theme.neutral}}>
                            {tipoDeAula(unit)}
                          </span>
                        )}
                      </span>

                      {unit.completed && (
                        <i
                          className="fa-solid fa-circle-check"
                          style={{color: theme.primary}}
                          aria-label="Concluída"
                        />
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </article>
      )}
    </div>
  );
};
